import React, { useState, useEffect, useCallback } from 'react';
import { stripeService, PaymentHistoryItem } from '../lib/stripeService';
import { invoiceService } from '../lib/invoiceService';
import { useAuth } from '../contexts/AuthContext';

interface PaymentHistoryProps {
  onClose: () => void;
}

export const PaymentHistory: React.FC<PaymentHistoryProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'payments' | 'invoices'>('payments');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPaymentHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Loading payment history for user:', user!.id);
      const history = await stripeService.getPaymentHistory(user!.id);
      console.log('📊 Payment history loaded:', history);
      setPayments(history);
    } catch (err) {
      console.error('❌ Error loading payment history:', err);
      setError('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadInvoiceHistory = useCallback(async () => {
    try {
      console.log('🔍 Loading invoice history for user:', user!.id);
      const history = await invoiceService.getInvoiceHistory(user!.id);
      console.log('📄 Invoice history loaded:', history);
      setInvoices(history);
    } catch (err) {
      console.error('❌ Error loading invoice history:', err);
      // Don't set error for invoices as it's optional
    }
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      loadPaymentHistory();
      loadInvoiceHistory();
    }
  }, [user?.id, loadPaymentHistory, loadInvoiceHistory]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'succeeded':
        return 'text-green-600 bg-green-100';
      case 'processing':
        return 'text-yellow-600 bg-yellow-100';
      case 'requires_payment_method':
        return 'text-gray-600 bg-gray-100';
      case 'canceled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'succeeded':
        return 'Completed';
      case 'processing':
        return 'Processing';
      case 'requires_payment_method':
        return 'Pending';
      case 'canceled':
        return 'Canceled';
      default:
        return status;
    }
  };

  const getPaymentSummary = () => {
    const completedPayments = payments.filter(p => p.status === 'succeeded');
    const totalTokens = completedPayments.reduce((sum, p) => sum + (p.tokens_purchased || 0), 0);
    
    return { totalTokens, completedCount: completedPayments.length };
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
                     <div className="flex justify-between items-center mb-4">
             <h2 className="text-xl font-semibold">Token Purchase History</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                         <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Purchase History</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (activeTab === 'payments') {
                  loadPaymentHistory();
                } else {
                  loadInvoiceHistory();
                }
              }}
              disabled={loading}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
                  Refreshing...
                </>
              ) : (
                'Refresh'
              )}
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'payments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Payments ({payments.length})
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'invoices'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Invoices ({invoices.length})
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {!loading && payments.length > 0 && (
                     <div className="mb-6 p-4 bg-blue-50 rounded-lg">
             <h3 className="font-semibold text-blue-900 mb-2">Purchase Summary</h3>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
             <div>
               <span className="text-blue-700">Total Tokens:</span>
               <span className="ml-2 font-medium text-blue-900">
                 {getPaymentSummary().totalTokens.toLocaleString()}
               </span>
             </div>
             <div>
               <span className="text-blue-700">Completed Purchases:</span>
               <span className="ml-2 font-medium text-blue-900">
                 {getPaymentSummary().completedCount}
               </span>
             </div>
           </div>
          </div>
        )}

                         {activeTab === 'payments' ? (
          !loading && payments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-4">📊</div>
              <p>No token purchase history found</p>
              <p className="text-sm mt-2">Your token purchase history will appear here once you buy tokens.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {payments.map((payment) => (
                <div
                  key={payment.payment_intent_id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                          {getStatusText(payment.status)}
                        </span>
                        {payment.package_name && (
                          <span className="text-sm text-gray-600">
                            {payment.package_name}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Tokens:</span>
                          <span className="ml-2 font-medium">
                            {payment.tokens_purchased ? payment.tokens_purchased.toLocaleString() : 'N/A'}
                          </span>
                        </div>
                        
                        <div>
                          <span className="text-gray-500">Date:</span>
                          <span className="ml-2">{formatDate(payment.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-xs text-gray-400 font-mono">
                        {payment.payment_intent_id.slice(-8)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          // Invoices Tab
          !loading && invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-4">📄</div>
              <p>No invoice history found</p>
              <p className="text-sm mt-2">Invoices will appear here once payments are processed.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <div
                  key={invoice.invoice_id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${invoiceService.getStatusColor(invoice.status)}`}>
                          {invoiceService.getStatusText(invoice.status)}
                        </span>
                        {invoice.invoice_number && (
                          <span className="text-sm text-gray-600">
                            #{invoice.invoice_number}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Amount:</span>
                          <span className="ml-2 font-medium">
                            {invoiceService.formatAmount(invoice.amount_cents)}
                          </span>
                        </div>
                        
                        <div>
                          <span className="text-gray-500">Date:</span>
                          <span className="ml-2">{invoiceService.formatDate(invoice.created_at)}</span>
                        </div>
                      </div>

                      {invoice.description && (
                        <div className="mt-2 text-sm text-gray-600">
                          {invoice.description}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      {invoice.invoice_pdf_url && (
                        <button
                          onClick={() => window.open(invoice.invoice_pdf_url, '_blank')}
                          className="text-xs text-green-600 hover:text-green-700 font-medium"
                        >
                          Download PDF
                        </button>
                      )}
                      
                      {invoice.hosted_invoice_url && (
                        <button
                          onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                          className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                        >
                          View Online
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

                 <div className="mt-6 pt-4 border-t border-gray-200">
           <div className="text-sm text-gray-500">
             <p>• Token purchase history is automatically updated when tokens are purchased</p>
             <p>• Contact support if you notice any discrepancies</p>
           </div>
         </div>
      </div>
    </div>
  );
}; 